/**
 * FeedInbox SDK - Secure TypeScript client for feedback collection
 * 
 * This SDK provides a secure interface to the FeedInbox API with:
 * - Domain validation
 * - API key security
 * - Rate limiting
 * - Retry logic
 * - Input sanitization
 */

// Types
export interface FeedInboxConfig {
  apiKey?: string; // Optional - can be provided via env variable
  apiUrl?: string;
  timeout?: number;
  retries?: number;
  customBackendUrl?: string;
}

export interface FeedbackData {
  userEmail: string;
  message: string;
  subject?: string;
  priority?: 'low' | 'medium' | 'high';
  workspaceId?: string;
  metadata?: Record<string, any>;
}

export interface SubscriptionObject {
  id: string;
  title: string;
  description: string;
}

export interface SubscriptionData {
  email: string;
  subscriptions: SubscriptionObject[]; // Array of subscription objects
  consent?: boolean;
  workspaceId?: string;
  metadata?: Record<string, any>;
}

export interface ContactData {
  firstName: string;
  lastName?: string;
  email: string;
  subject?: string;
  message: string;
  workspaceId?: string;
  metadata?: Record<string, any>;
}

export interface PreferenceObject {
  id: string;
  title: string;
  description: string;
  value?: any;
}

export interface PreferencesData {
  preferences: PreferenceObject[]; // Array of preference objects
  workspaceId?: string;
  metadata?: Record<string, any>;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}


export interface UnsubscribeInfo {
  email: string;
  subscriptions: SubscriptionObject[];
  active: boolean;
  token: string;
  requires_confirmation: boolean;
}

export interface UnsubscribeResult {
  email: string;
  unsubscribed_from: string;
  remaining_subscriptions: SubscriptionObject[];
  completely_unsubscribed: boolean;
}

// Security utilities
class SecurityUtils {
  static validateApiKey(apiKey: string): boolean {
    if (!apiKey || typeof apiKey !== 'string') return false;
    if (!apiKey.startsWith('fb_')) return false;
    if (apiKey.length < 10) return false;
    return true;
  }

  static validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  static sanitizeString(str: string): string {
    if (typeof str !== 'string') return '';
    return str.trim().slice(0, 10000); // Max 10k chars
  }

  static validateUrl(url: string): boolean {
    try {
      const parsed = new URL(url);
      return ['http:', 'https:'].includes(parsed.protocol);
    } catch {
      return false;
    }
  }
}

// Rate limiter
class RateLimiter {
  private requests: number[] = [];
  private readonly maxRequests: number;
  private readonly timeWindow: number;

  constructor(maxRequests = 100, timeWindowMs = 60000) {
    this.maxRequests = maxRequests;
    this.timeWindow = timeWindowMs;
  }

  canMakeRequest(): boolean {
    const now = Date.now();
    this.requests = this.requests.filter(time => now - time < this.timeWindow);
    
    if (this.requests.length >= this.maxRequests) {
      return false;
    }
    
    this.requests.push(now);
    return true;
  }
}

// Main SDK class
export class FeedInboxSDK {
  private readonly apiKey: string;
  private readonly apiUrl: string;
  private readonly timeout: number;
  private readonly retries: number;
  private readonly rateLimiter: RateLimiter;
  private readonly headers: Record<string, string>;

  constructor(config: FeedInboxConfig = {}) {
    // Get API key from config or environment variable
    const apiKey = config.apiKey || this.getApiKeyFromEnv();
    
    if (!apiKey) {
      throw new Error('API key is required. Provide it in config or set FEEDINBOX_API_KEY environment variable');
    }

    if (!SecurityUtils.validateApiKey(apiKey)) {
      throw new Error('Invalid API key format. Must start with "fb_" and be at least 10 characters');
    }

    this.apiKey = apiKey;
    this.apiUrl = config.customBackendUrl || config.apiUrl || this.getDefaultApiUrl();
    this.timeout = config.timeout || 10000; // 10s default
    this.retries = config.retries || 2;
    this.rateLimiter = new RateLimiter();

    // Validate API URL
    if (!SecurityUtils.validateUrl(this.apiUrl)) {
      throw new Error('Invalid API URL provided');
    }

    this.headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.apiKey}`,
      'User-Agent': 'FeedInbox-SDK/1.0.0',
      'X-SDK-Version': '1.0.0',
      // Add Origin header for server-side requests to match domain validation
      'Origin': typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000',
    };
  }

  private getApiKeyFromEnv(): string | undefined {
    // Check for environment variable in both browser and Node.js
    if (typeof process !== 'undefined' && process.env) {
      return process.env.FEEDINBOX_API_KEY;
    }
    
    // In browser environments, check for global variables (set by bundler)
    if (typeof window !== 'undefined' && (window as any).FEEDINBOX_API_KEY) {
      return (window as any).FEEDINBOX_API_KEY;
    }
    
    return undefined;
  }

  private getDefaultApiUrl(): string {
    if (typeof window !== 'undefined') {
      // Browser environment - use widget proxy port
      const protocol = window.location.protocol;
      const hostname = window.location.hostname;
      const port = hostname === 'localhost' ? ':3103' : '';
      return `${protocol}//${hostname}${port}`;
    }
    // Node.js environment - default to widget proxy
    return process.env.FEEDINBOX_API_URL || 'http://localhost:3103';
  }


  private async makeRequest<T>(
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' = 'GET',
    data?: any,
    attempt = 1
  ): Promise<ApiResponse<T>> {
    // Rate limiting check
    if (!this.rateLimiter.canMakeRequest()) {
      return {
        success: false,
        error: 'Rate limit exceeded. Please try again later.'
      };
    }

    const url = `${this.apiUrl}${endpoint}`;
    
    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    const config: RequestInit = {
      method,
      headers: this.headers,
      signal: controller.signal,
    };

    if (data && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
      config.body = JSON.stringify(data);
    }

    // Debug logging removed for security

    try {
      const response = await fetch(url, config);
      clearTimeout(timeoutId);

      const result = await response.json();

      if (!response.ok) {
        // Debug logging removed for security
        
        // Retry on server errors (5xx) and some 4xx errors
        if (attempt < this.retries && (response.status >= 500 || response.status === 429)) {
          await this.delay(Math.pow(2, attempt) * 1000); // Exponential backoff
          return this.makeRequest(endpoint, method, data, attempt + 1);
        }

        return {
          success: false,
          error: result.error || `HTTP error! status: ${response.status}`
        };
      }

      // Debug logging removed for security
      return result;
    } catch (error) {
      clearTimeout(timeoutId);
      // Debug logging removed for security

      // Retry on network errors
      if (attempt < this.retries) {
        await this.delay(Math.pow(2, attempt) * 1000);
        return this.makeRequest(endpoint, method, data, attempt + 1);
      }

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error'
      };
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // ============================================================================
  // FEEDBACK CRUD OPERATIONS
  // ============================================================================

  /**
   * Create feedback entry
   */
  async createFeedback(feedback: FeedbackData): Promise<ApiResponse> {
    if (!feedback.userEmail || !feedback.message) {
      return {
        success: false,
        error: 'userEmail and message are required'
      };
    }

    if (!SecurityUtils.validateEmail(feedback.userEmail)) {
      return {
        success: false,
        error: 'Invalid email format'
      };
    }

    console.log('feedback', feedback);

    const sanitizedData = {
      email: SecurityUtils.sanitizeString(feedback.userEmail),
      message: SecurityUtils.sanitizeString(feedback.message),
      subject: feedback.subject ? SecurityUtils.sanitizeString(feedback.subject) : 'Widget Feedback',
      priority: feedback.priority || 'medium',
      workspace_id: feedback.workspaceId,
      page_url: typeof window !== 'undefined' ? window.location.href : undefined,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      metadata: feedback.metadata,
      timestamp: new Date().toISOString()
    };

    return this.makeRequest('/api/widget/v1/feedback', 'POST', sanitizedData);
  }

  /**
   * Get feedback entries
   */
  async getFeedback(params?: { 
    userEmail?: string; 
    limit?: number; 
    page?: number; 
    status?: 'open' | 'responded' | 'closed' 
  }): Promise<ApiResponse> {
    const queryParams = new URLSearchParams();
    
    if (params?.userEmail) {
      if (!SecurityUtils.validateEmail(params.userEmail)) {
        return {
          success: false,
          error: 'Invalid email format'
        };
      }
      queryParams.append('user_email', SecurityUtils.sanitizeString(params.userEmail));
    }
    
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.status) queryParams.append('status', params.status);

    const query = queryParams.toString();
    const endpoint = `/api/widget/v1/feedback${query ? `?${query}` : ''}`;
    
    return this.makeRequest(endpoint, 'GET');
  }

  /**
   * Update feedback entry
   */
  async updateFeedback(feedbackId: string, updates: Partial<FeedbackData>): Promise<ApiResponse> {
    if (!feedbackId) {
      return {
        success: false,
        error: 'feedbackId is required'
      };
    }

    const sanitizedData = {
      feedback_id: SecurityUtils.sanitizeString(feedbackId),
      ...updates,
      timestamp: new Date().toISOString()
    };

    return this.makeRequest(`/api/widget/v1/feedback/${feedbackId}`, 'PUT', sanitizedData);
  }

  /**
   * Delete feedback entry
   */
  async deleteFeedback(feedbackId: string): Promise<ApiResponse> {
    if (!feedbackId) {
      return {
        success: false,
        error: 'feedbackId is required'
      };
    }

    return this.makeRequest(`/api/widget/v1/feedback/${feedbackId}`, 'DELETE');
  }

  // ============================================================================
  // SUBSCRIBERS CRUD OPERATIONS (newsletter subscriptions)
  // ============================================================================

  /**
   * Create subscriber (newsletter subscription)
   */
  async createSubscriber(subscription: SubscriptionData): Promise<ApiResponse> {
    // Allow empty subscriptions array for "unsubscribe from all" operations
    if (!subscription.email || !Array.isArray(subscription.subscriptions)) {
      return {
        success: false,
        error: 'email and subscriptions array are required'
      };
    }

    if (!SecurityUtils.validateEmail(subscription.email)) {
      return {
        success: false,
        error: 'Invalid email format'
      };
    }

    const sanitizedData = {
      email: SecurityUtils.sanitizeString(subscription.email),
      subscriptions: subscription.subscriptions.map(sub => ({
        id: SecurityUtils.sanitizeString(sub.id),
        title: SecurityUtils.sanitizeString(sub.title),
        description: SecurityUtils.sanitizeString(sub.description)
      })),
      workspace_id: subscription.workspaceId,
      page_url: typeof window !== 'undefined' ? window.location.href : undefined,
      consent_given: subscription.consent !== false,
      source: 'widget',
      metadata: subscription.metadata,
      timestamp: new Date().toISOString()
    };

    return this.makeRequest('/api/widget/v1/subscribers', 'POST', sanitizedData);
  }

  /**
   * Get subscriber data
   */
  async getSubscriber(userEmail: string): Promise<ApiResponse> {
    if (!userEmail) {
      return {
        success: false,
        error: 'userEmail is required'
      };
    }

    if (!SecurityUtils.validateEmail(userEmail)) {
      return {
        success: false,
        error: 'Invalid email format'
      };
    }

    const sanitizedEmail = SecurityUtils.sanitizeString(userEmail);
    return this.makeRequest(`/api/widget/v1/subscribers?email=${encodeURIComponent(sanitizedEmail)}`, 'GET');
  }

  /**
   * Update subscriber subscriptions
   */
  async updateSubscriber(userEmail: string, subscriptions: SubscriptionObject[]): Promise<ApiResponse> {
    if (!userEmail) {
      return {
        success: false,
        error: 'userEmail is required'
      };
    }

    if (!SecurityUtils.validateEmail(userEmail)) {
      return {
        success: false,
        error: 'Invalid email format'
      };
    }

    if (!Array.isArray(subscriptions)) {
      return {
        success: false,
        error: 'subscriptions must be an array'
      };
    }

    const sanitizedData = {
      email: SecurityUtils.sanitizeString(userEmail),
      subscriptions: subscriptions.map(sub => ({
        id: SecurityUtils.sanitizeString(sub.id),
        title: SecurityUtils.sanitizeString(sub.title),
        description: SecurityUtils.sanitizeString(sub.description)
      })),
      timestamp: new Date().toISOString()
    };

    return this.makeRequest('/api/widget/v1/subscribers', 'PUT', sanitizedData);
  }

  /**
   * Delete subscriber
   */
  async deleteSubscriber(userEmail: string): Promise<ApiResponse> {
    if (!userEmail) {
      return {
        success: false,
        error: 'userEmail is required'
      };
    }

    if (!SecurityUtils.validateEmail(userEmail)) {
      return {
        success: false,
        error: 'Invalid email format'
      };
    }

    const sanitizedEmail = SecurityUtils.sanitizeString(userEmail);
    return this.makeRequest(`/api/widget/v1/subscribers?email=${encodeURIComponent(sanitizedEmail)}`, 'DELETE');
  }

  // ============================================================================
  // CONTACTS CRUD OPERATIONS
  // ============================================================================

  /**
   * Create contact form submission
   */
  async createContact(contact: ContactData): Promise<ApiResponse> {
    if (!contact.firstName || !contact.email || !contact.message) {
      return {
        success: false,
        error: 'firstName, email, and message are required'
      };
    }

    if (!SecurityUtils.validateEmail(contact.email)) {
      return {
        success: false,
        error: 'Invalid email format'
      };
    }

    const sanitizedData = {
      first_name: SecurityUtils.sanitizeString(contact.firstName),
      last_name: contact.lastName ? SecurityUtils.sanitizeString(contact.lastName) : undefined,
      email: SecurityUtils.sanitizeString(contact.email),
      subject: contact.subject ? SecurityUtils.sanitizeString(contact.subject) : 'Contact Form Submission',
      message: SecurityUtils.sanitizeString(contact.message),
      workspace_id: contact.workspaceId,
      page_url: typeof window !== 'undefined' ? window.location.href : undefined,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      metadata: contact.metadata,
      timestamp: new Date().toISOString()
    };

    return this.makeRequest('/api/widget/v1/contact', 'POST', sanitizedData);
  }

  /**
   * Get contact submissions
   */
  async getContacts(params?: {
    userEmail?: string;
    limit?: number;
    page?: number;
  }): Promise<ApiResponse> {
    const queryParams = new URLSearchParams();
    
    if (params?.userEmail) {
      if (!SecurityUtils.validateEmail(params.userEmail)) {
        return {
          success: false,
          error: 'Invalid email format'
        };
      }
      queryParams.append('user_email', SecurityUtils.sanitizeString(params.userEmail));
    }
    
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.page) queryParams.append('page', params.page.toString());

    const query = queryParams.toString();
    const endpoint = `/api/widget/v1/contact${query ? `?${query}` : ''}`;
    
    return this.makeRequest(endpoint, 'GET');
  }

  /**
   * Update contact submission
   */
  async updateContact(contactId: string, updates: Partial<ContactData>): Promise<ApiResponse> {
    if (!contactId) {
      return {
        success: false,
        error: 'contactId is required'
      };
    }

    const sanitizedData = {
      contact_id: SecurityUtils.sanitizeString(contactId),
      ...updates,
      timestamp: new Date().toISOString()
    };

    return this.makeRequest(`/api/widget/v1/contact/${contactId}`, 'PUT', sanitizedData);
  }

  /**
   * Delete contact submission
   */
  async deleteContact(contactId: string): Promise<ApiResponse> {
    if (!contactId) {
      return {
        success: false,
        error: 'contactId is required'
      };
    }

    return this.makeRequest(`/api/widget/v1/contact/${contactId}`, 'DELETE');
  }

  // ============================================================================
  // PREFERENCES CRUD OPERATIONS (separate from subscribers!)
  // ============================================================================

  /**
   * Create user preferences
   */
  async createPreferences(userEmail: string, preferences: PreferencesData): Promise<ApiResponse> {
    if (!userEmail) {
      return {
        success: false,
        error: 'userEmail is required'
      };
    }

    if (!SecurityUtils.validateEmail(userEmail)) {
      return {
        success: false,
        error: 'Invalid email format'
      };
    }

    // Allow empty preferences array for "clear all preferences" operations
    if (!Array.isArray(preferences.preferences)) {
      return {
        success: false,
        error: 'preferences array is required'
      };
    }

    const sanitizedData = {
      preferences: preferences.preferences.map(pref => ({
        id: SecurityUtils.sanitizeString(pref.id),
        title: SecurityUtils.sanitizeString(pref.title),
        description: SecurityUtils.sanitizeString(pref.description),
        value: pref.value
      })),
      user_email: SecurityUtils.sanitizeString(userEmail),
      workspace_id: preferences.workspaceId,
      page_url: typeof window !== 'undefined' ? window.location.href : undefined,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      metadata: preferences.metadata,
      timestamp: new Date().toISOString()
    };

    return this.makeRequest('/api/widget/v1/preferences', 'POST', sanitizedData);
  }

  /**
   * Get user preferences
   */
  async getPreferences(userEmail: string): Promise<ApiResponse> {
    if (!userEmail) {
      return {
        success: false,
        error: 'userEmail is required'
      };
    }

    if (!SecurityUtils.validateEmail(userEmail)) {
      return {
        success: false,
        error: 'Invalid email format'
      };
    }

    const sanitizedEmail = SecurityUtils.sanitizeString(userEmail);
    return this.makeRequest(`/api/widget/v1/preferences?user_email=${encodeURIComponent(sanitizedEmail)}`, 'GET');
  }

  /**
   * Update user preferences
   */
  async updatePreferences(userEmail: string, preferences: PreferencesData): Promise<ApiResponse> {
    if (!userEmail) {
      return {
        success: false,
        error: 'userEmail is required'
      };
    }

    if (!SecurityUtils.validateEmail(userEmail)) {
      return {
        success: false,
        error: 'Invalid email format'
      };
    }

    // Allow empty preferences array for "clear all preferences" operations
    if (!Array.isArray(preferences.preferences)) {
      return {
        success: false,
        error: 'preferences array is required'
      };
    }

    const sanitizedData = {
      preferences: preferences.preferences.map(pref => ({
        id: SecurityUtils.sanitizeString(pref.id),
        title: SecurityUtils.sanitizeString(pref.title),
        description: SecurityUtils.sanitizeString(pref.description),
        value: pref.value
      })),
      user_email: SecurityUtils.sanitizeString(userEmail),
      workspace_id: preferences.workspaceId,
      page_url: typeof window !== 'undefined' ? window.location.href : undefined,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      metadata: preferences.metadata,
      timestamp: new Date().toISOString()
    };

    return this.makeRequest('/api/widget/v1/preferences', 'PUT', sanitizedData);
  }

  /**
   * Delete user preferences
   */
  async deletePreferences(userEmail: string): Promise<ApiResponse> {
    if (!userEmail) {
      return {
        success: false,
        error: 'userEmail is required'
      };
    }

    if (!SecurityUtils.validateEmail(userEmail)) {
      return {
        success: false,
        error: 'Invalid email format'
      };
    }

    const sanitizedEmail = SecurityUtils.sanitizeString(userEmail);
    return this.makeRequest(`/api/widget/v1/preferences?user_email=${encodeURIComponent(sanitizedEmail)}`, 'DELETE');
  }

  // ============================================================================
  // ESSENTIAL OPTIMIZED METHODS
  // ============================================================================

  /**
   * Combined user data fetch - gets preferences, subscriptions, and metadata in one call
   * Optimized to replace multiple separate API calls
   */
  async getUserProfile(userEmail: string, options?: { 
    includePreferences?: boolean; 
    includeSubscriptions?: boolean; 
    includeMetadata?: boolean;
    preferenceIds?: string[];
    subscriptionIds?: string[];
  }): Promise<ApiResponse> {
    if (!userEmail) {
      return {
        success: false,
        error: 'userEmail is required'
      };
    }

    if (!SecurityUtils.validateEmail(userEmail)) {
      return {
        success: false,
        error: 'Invalid email format'
      };
    }

    const sanitizedEmail = SecurityUtils.sanitizeString(userEmail);
    const queryParams = new URLSearchParams();
    queryParams.append('email', sanitizedEmail);
    
    // Build query params based on options
    const opts = options || { includePreferences: true, includeSubscriptions: true };
    if (opts.includePreferences) queryParams.append('include_preferences', 'true');
    if (opts.includeSubscriptions) queryParams.append('include_subscriptions', 'true');
    if (opts.includeMetadata) queryParams.append('include_metadata', 'true');
    if (opts.preferenceIds?.length) queryParams.append('preference_ids', opts.preferenceIds.join(','));
    if (opts.subscriptionIds?.length) queryParams.append('subscription_ids', opts.subscriptionIds.join(','));

    return this.makeRequest(`/api/widget/v1/users/profile?${queryParams.toString()}`, 'GET');
  }

  /**
   * Check if user has any data (preferences or subscriptions) without fetching full objects
   * Optimized for existence checks
   */
  async hasUserData(userEmail: string): Promise<ApiResponse<{ hasPreferences: boolean; hasSubscriptions: boolean; hasAnyData: boolean }>> {
    if (!userEmail) {
      return {
        success: false,
        error: 'userEmail is required'
      };
    }

    if (!SecurityUtils.validateEmail(userEmail)) {
      return {
        success: false,
        error: 'Invalid email format'
      };
    }

    const sanitizedEmail = SecurityUtils.sanitizeString(userEmail);
    return this.makeRequest(`/api/widget/v1/users/data-check?email=${encodeURIComponent(sanitizedEmail)}`, 'GET');
  }

  /**
   * Toggle single subscription on/off by ID - optimized for subscription management
   */
  async toggleSubscription(userEmail: string, subscriptionId: string, enabled: boolean, metadata?: Record<string, any>): Promise<ApiResponse> {
    if (!userEmail || !subscriptionId) {
      return {
        success: false,
        error: 'userEmail and subscriptionId are required'
      };
    }

    if (!SecurityUtils.validateEmail(userEmail)) {
      return {
        success: false,
        error: 'Invalid email format'
      };
    }

    const sanitizedData = {
      email: SecurityUtils.sanitizeString(userEmail),
      subscription_id: SecurityUtils.sanitizeString(subscriptionId),
      enabled,
      metadata: {
        action: enabled ? 'enable_subscription' : 'disable_subscription',
        ...metadata
      },
      timestamp: new Date().toISOString()
    };

    return this.makeRequest('/api/widget/v1/subscribers/toggle', 'PATCH', sanitizedData);
  }

  // ============================================================================
  // LEGACY/CONVENIENCE METHODS FOR BACKWARD COMPATIBILITY
  // ============================================================================

  /**
   * Subscribe to newsletter (alias for createSubscriber)
   * @deprecated Use createSubscriber instead
   */
  async subscribe(subscription: SubscriptionData): Promise<ApiResponse> {
    return this.createSubscriber(subscription);
  }

  /**
   * Submit contact form (alias for createContact)
   * @deprecated Use createContact instead
   */
  async submitContact(contact: ContactData): Promise<ApiResponse> {
    return this.createContact(contact);
  }


}

// Export everything
export default FeedInboxSDK;